import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert } from 'react-bootstrap';
import axios from '../utils/api';
import '../styles/Settings.css';

const generatePassword = (length = 12) => {
  const upper = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
  const lower = 'abcdefghijkmnopqrstuvwxyz';
  const digits = '23456789';
  const symbols = '!@#$%&*?';
  const all = upper + lower + digits + symbols;
  const pick = (chars) => chars[Math.floor(Math.random() * chars.length)];
  const required = [pick(upper), pick(lower), pick(digits), pick(symbols)];
  const rest = Array.from({ length: length - required.length }, () => pick(all));
  const chars = [...required, ...rest];
  for (let i = chars.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [chars[i], chars[j]] = [chars[j], chars[i]];
  }
  return chars.join('');
};

const emptyForm = {
  full_name: '',
  email: '',
  password: '',
  confirm_password: '',
  role: 'user',
};

const ManageStaff = () => {
  const navigate = useNavigate();
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const [staff, setStaff] = useState([]);
  const [filter, setFilter] = useState('all'); // all | active | inactive
  const [form, setForm] = useState(emptyForm);
  const [showPassword, setShowPassword] = useState(false);
  const [copied, setCopied] = useState(false);
  const [status, setStatus] = useState({ success: null, message: '' });
  const [loading, setLoading] = useState(false);
  const [listLoading, setListLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const loadStaff = useCallback(async () => {
    setListLoading(true);
    try {
      const res = await axios.get('/auth/staff/');
      setStaff(res.data?.users || []);
    } catch (err) {
      setStatus({
        success: false,
        message: err.response?.data?.error || 'Failed to load staff list.',
      });
    } finally {
      setListLoading(false);
    }
  }, []);

  useEffect(() => {
    document.title = 'Manage Staff';
    if (currentUser?.role !== 'master_admin') {
      navigate('/dashboard', { replace: true });
      return;
    }
    loadStaff();
  }, [currentUser?.role, navigate, loadStaff]);

  if (currentUser?.role !== 'master_admin') {
    return null;
  }

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setCopied(false);
  };

  const handleGeneratePassword = () => {
    const password = generatePassword(12);
    setForm((prev) => ({ ...prev, password, confirm_password: password }));
    setShowPassword(true);
    setCopied(false);
  };

  const handleCopyPassword = async () => {
    if (!form.password) return;
    try {
      await navigator.clipboard.writeText(form.password);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setStatus({ success: false, message: 'Could not copy password to clipboard.' });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ success: null, message: '' });

    if (form.password !== form.confirm_password) {
      setStatus({ success: false, message: 'Passwords do not match.' });
      return;
    }
    if (form.password.length < 8) {
      setStatus({ success: false, message: 'Password must be at least 8 characters.' });
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post('/auth/staff/', {
        full_name: form.full_name.trim(),
        email: form.email.trim(),
        password: form.password,
        role: form.role,
      });
      setStatus({
        success: true,
        message: res.data?.message || 'Staff account created successfully.',
      });
      setForm(emptyForm);
      setShowPassword(false);
      setCopied(false);
      setShowCreate(false);
      await loadStaff();
    } catch (err) {
      const data = err.response?.data;
      setStatus({
        success: false,
        message: data?.error || data?.email?.[0] || data?.detail || 'Failed to create staff account.',
      });
    } finally {
      setLoading(false);
    }
  };

  const updateStaff = async (id, payload, successMessage) => {
    setBusyId(id);
    setStatus({ success: null, message: '' });
    try {
      const res = await axios.patch(`/auth/staff/${id}/`, payload);
      setStatus({ success: true, message: successMessage || res.data?.message || 'Updated.' });
      await loadStaff();
    } catch (err) {
      setStatus({
        success: false,
        message: err.response?.data?.error || 'Update failed.',
      });
    } finally {
      setBusyId(null);
    }
  };

  const deleteStaff = async (member) => {
    const label = member.full_name ? `${member.full_name} (${member.email})` : member.email;
    if (!window.confirm(`Delete ${label}? This cannot be undone.`)) {
      return;
    }
    setBusyId(member.id);
    setStatus({ success: null, message: '' });
    try {
      const res = await axios.delete(`/auth/staff/${member.id}/`);
      setStatus({ success: true, message: res.data?.message || `${member.email} has been deleted.` });
      await loadStaff();
    } catch (err) {
      setStatus({
        success: false,
        message: err.response?.data?.error || 'Delete failed.',
      });
    } finally {
      setBusyId(null);
    }
  };

  const filtered = staff.filter((u) => {
    if (filter === 'active') return u.is_active;
    if (filter === 'inactive') return !u.is_active;
    return true;
  });

  return (
    <div className="settings-page">
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
        <div>
          <h1>Manage Staff</h1>
          <p>Create accounts, change roles, deactivate access, or delete users</p>
        </div>
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => setShowCreate((v) => !v)}
        >
          <i className={`fas ${showCreate ? 'fa-times' : 'fa-user-plus'}`} style={{ marginRight: 8 }}></i>
          {showCreate ? 'Close form' : 'Add staff'}
        </button>
      </div>

      {status.message && (
        <Alert variant={status.success ? 'success' : 'danger'} className="settings-alert">
          {status.message}
        </Alert>
      )}

      {showCreate && (
        <div className="settings-section">
          <div className="section-header">
            <i className="fas fa-user-plus"></i>
            <h2>New Staff Account</h2>
          </div>

          <form onSubmit={handleSubmit} className="settings-form">
            <div className="form-group">
              <label htmlFor="full_name">Full Name</label>
              <input
                id="full_name"
                type="text"
                name="full_name"
                className="form-control"
                value={form.full_name}
                onChange={handleChange}
                placeholder="Jane Doe"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="email">Email Address</label>
              <input
                id="email"
                type="email"
                name="email"
                className="form-control"
                value={form.email}
                onChange={handleChange}
                placeholder="staff@company.com"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="role">Role</label>
              <select
                id="role"
                name="role"
                className="form-control"
                value={form.role}
                onChange={handleChange}
              >
                <option value="user">Staff</option>
                <option value="master_admin">Master Admin</option>
              </select>
            </div>

            <div className="form-group">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <label htmlFor="password" style={{ marginBottom: 0 }}>Password (min 8 characters)</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={handleGeneratePassword}
                    style={{ padding: '6px 12px', fontSize: 13 }}
                  >
                    <i className="fas fa-sync-alt" style={{ marginRight: 6 }}></i>
                    Auto generate
                  </button>
                  {form.password && (
                    <button
                      type="button"
                      className="btn btn-outline-primary"
                      onClick={handleCopyPassword}
                      style={{ padding: '6px 12px', fontSize: 13 }}
                    >
                      <i className={`fas ${copied ? 'fa-check' : 'fa-copy'}`} style={{ marginRight: 6 }}></i>
                      {copied ? 'Copied' : 'Copy'}
                    </button>
                  )}
                </div>
              </div>
              <div style={{ position: 'relative' }}>
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  className="form-control"
                  value={form.password}
                  onChange={handleChange}
                  placeholder="Create a password or auto generate"
                  required
                  minLength={8}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    position: 'absolute',
                    right: 12,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    border: 'none',
                    background: 'transparent',
                    cursor: 'pointer',
                    color: '#64748b',
                  }}
                  aria-label="Toggle password visibility"
                >
                  <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                </button>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="confirm_password">Confirm Password</label>
              <input
                id="confirm_password"
                type={showPassword ? 'text' : 'password'}
                name="confirm_password"
                className="form-control"
                value={form.confirm_password}
                onChange={handleChange}
                placeholder="Re-enter password"
                required
                minLength={8}
              />
            </div>

            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Creating…' : 'Create Staff'}
            </button>
          </form>
        </div>
      )}

      <div className="settings-section">
        <div className="section-header" style={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <i className="fas fa-users"></i>
            <h2>Team Members</h2>
          </div>
          <select
            className="form-control"
            style={{ width: 'auto', minWidth: 160 }}
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          >
            <option value="all">All</option>
            <option value="active">Active</option>
            <option value="inactive">Deactivated</option>
          </select>
        </div>

        {listLoading ? (
          <p>Loading staff…</p>
        ) : !filtered.length ? (
          <p style={{ color: '#64748b' }}>No staff found for this filter.</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #e2e8f0', textAlign: 'left' }}>
                  <th style={{ padding: '10px 8px' }}>Name</th>
                  <th style={{ padding: '10px 8px' }}>Email</th>
                  <th style={{ padding: '10px 8px' }}>Role</th>
                  <th style={{ padding: '10px 8px' }}>Status</th>
                  <th style={{ padding: '10px 8px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((member) => {
                  const isSelf = member.id === currentUser.id;
                  const busy = busyId === member.id;
                  return (
                    <tr key={member.id} style={{ borderBottom: '1px solid #e2e8f0', opacity: member.is_active ? 1 : 0.7 }}>
                      <td style={{ padding: '12px 8px' }}>
                        {member.full_name}
                        {isSelf && <small style={{ color: '#64748b', marginLeft: 6 }}>(you)</small>}
                      </td>
                      <td style={{ padding: '12px 8px' }}>{member.email}</td>
                      <td style={{ padding: '12px 8px' }}>
                        <select
                          className="form-control"
                          style={{ minWidth: 140 }}
                          value={member.role}
                          disabled={busy || isSelf}
                          onChange={(e) =>
                            updateStaff(member.id, { role: e.target.value }, `Role updated for ${member.email}.`)
                          }
                        >
                          <option value="user">Staff</option>
                          <option value="master_admin">Master Admin</option>
                        </select>
                      </td>
                      <td style={{ padding: '12px 8px' }}>
                        <span
                          style={{
                            display: 'inline-block',
                            padding: '4px 10px',
                            borderRadius: 999,
                            fontSize: 12,
                            fontWeight: 600,
                            background: member.is_active ? '#dcfce7' : '#fee2e2',
                            color: member.is_active ? '#166534' : '#991b1b',
                          }}
                        >
                          {member.is_active ? 'Active' : 'Deactivated'}
                        </span>
                      </td>
                      <td style={{ padding: '12px 8px' }}>
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                          <button
                            type="button"
                            className={`btn ${member.is_active ? 'btn-warning' : 'btn-secondary'}`}
                            style={{ padding: '6px 12px', fontSize: 13 }}
                            disabled={busy || isSelf}
                            onClick={() =>
                              updateStaff(
                                member.id,
                                { is_active: !member.is_active },
                                member.is_active
                                  ? `${member.email} has been deactivated.`
                                  : `${member.email} has been reactivated.`
                              )
                            }
                          >
                            {busy
                              ? '…'
                              : member.is_active
                                ? 'Deactivate'
                                : 'Activate'}
                          </button>
                          <button
                            type="button"
                            className="btn btn-danger"
                            style={{ padding: '6px 12px', fontSize: 13 }}
                            disabled={busy || isSelf}
                            onClick={() => deleteStaff(member)}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ManageStaff;
